import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import { retryUnlessRefused, workspacePath } from './workspaces'
import { opportunityKeys } from './opportunities'
import type { ApiEnvelope } from '@/types/api'
import type {
  IntelligenceTask,
  MetricTarget,
  Origin,
  OriginRef,
  SharedInsight,
  TaskPriority,
  TaskStatus,
} from '@/types/intelligence'

/**
 * The action loop (P5, P6): follow-ups, shares, objectives and My
 * Attention, each read from and written to its endpoint. Every change here
 * can move a count elsewhere (a follow-up assigned shows on its opportunity
 * and on the Overview), so a change refreshes all of them.
 */

/** A colleague a follow-up can be assigned to, or a share sent to: any active member. */
export interface Colleague {
  userId: string
  name: string
  /** Whether they can open Intelligence; without it, they see it once the Owner gives them a seat. */
  hasIntelligence: boolean
}

/** My Attention's four queues (P6 §3.1). */
export interface Attention {
  today: string
  overdue: IntelligenceTask[]
  assigned: IntelligenceTask[]
  shared: SharedInsight[]
  objectives: MetricTarget[]
}

/** The follow-ups that share an origin: an initiative (P5 §4). */
export interface Initiative {
  origin: Origin
  followUps: IntelligenceTask[]
  completed: number
  total: number
}

export type ObjectiveFilter = 'ACTIVE' | 'ACHIEVED' | 'ALL'

export interface NewTask {
  /** What it follows up, or the share it is raised from. */
  origin?: OriginRef
  sharedInsightId?: string
  title: string
  note?: string
  priority: TaskPriority
  dueDate?: string
  assignedToId: string
}

export interface NewShare {
  origin: OriginRef
  recipientIds: string[]
  note?: string
}

export interface NewObjective {
  /** One of the five metrics, or a care journey share (DECISIONS.md #45). */
  metricId: string
  title: string
  targetValue: number
  targetDate: string
  opportunityId?: string
}

export type ObjectiveChange = Partial<Pick<NewObjective, 'title' | 'targetValue' | 'targetDate'>>

export const actionKeys = {
  all: (workspaceId: string) => ['workspaces', workspaceId, 'actions'] as const,
  colleagues: (workspaceId: string) => ['workspaces', workspaceId, 'colleagues'] as const,
  attention: (workspaceId: string) => [...actionKeys.all(workspaceId), 'attention'] as const,
  initiatives: (workspaceId: string) => [...actionKeys.all(workspaceId), 'initiatives'] as const,
  tasks: (workspaceId: string, origin: OriginRef) => [...actionKeys.all(workspaceId), 'tasks', origin.kind, origin.id] as const,
  objectives: (workspaceId: string, filter: ObjectiveFilter) => [...actionKeys.all(workspaceId), 'objectives', filter] as const,
}

const path = (workspaceId: MaybeRefOrGetter<string>, rest: string): string => `${workspacePath(toValue(workspaceId))}${rest}`

/** After any change: the loop's own lists, and the opportunities that count their follow-ups. */
const useRefresh = (workspaceId: MaybeRefOrGetter<string>) => {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: actionKeys.all(toValue(workspaceId)) }),
      queryClient.invalidateQueries({ queryKey: opportunityKeys.all(toValue(workspaceId)) }),
    ])
  }
}

export const useColleagues = (workspaceId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: computed(() => actionKeys.colleagues(toValue(workspaceId))),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<Colleague[]>>(path(workspaceId, '/colleagues'))).data),
    enabled: computed(() => toValue(enabled)),
    retry: retryUnlessRefused,
  })

export const useAttention = (workspaceId: MaybeRefOrGetter<string>) =>
  useQuery({
    queryKey: computed(() => actionKeys.attention(toValue(workspaceId))),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<Attention>>(path(workspaceId, '/attention'))).data),
    retry: retryUnlessRefused,
  })

export const useInitiatives = (workspaceId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: computed(() => actionKeys.initiatives(toValue(workspaceId))),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<Initiative[]>>(path(workspaceId, '/initiatives'))).data),
    enabled: computed(() => toValue(enabled)),
    retry: retryUnlessRefused,
  })

/** The follow-ups of one opportunity, objective or report. */
export const useTasks = (workspaceId: MaybeRefOrGetter<string>, origin: MaybeRefOrGetter<OriginRef | null>) =>
  useQuery({
    queryKey: computed(() => {
      const ref = toValue(origin)
      return ref ? actionKeys.tasks(toValue(workspaceId), ref) : [...actionKeys.all(toValue(workspaceId)), 'tasks', 'none']
    }),
    queryFn: async () => {
      const ref = toValue(origin) as OriginRef
      return unwrap(
        (await http.get<ApiEnvelope<IntelligenceTask[]>>(path(workspaceId, '/tasks'), { params: { originKind: ref.kind, originId: ref.id } })).data,
      )
    },
    enabled: computed(() => toValue(origin) !== null),
    retry: retryUnlessRefused,
  })

export const useCreateTask = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async (task: NewTask) => unwrap((await http.post<ApiEnvelope<IntelligenceTask>>(path(workspaceId, '/tasks'), task)).data),
    onSuccess: refresh,
  })
}

export const useSetTaskStatus = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async ({ id, from, status, completionNote }: { id: string; from: TaskStatus; status: TaskStatus; completionNote?: string }) =>
      unwrap(
        (
          await http.patch<ApiEnvelope<IntelligenceTask>>(path(workspaceId, `/tasks/${encodeURIComponent(id)}/status`), {
            from,
            status,
            ...(completionNote ? { completionNote } : {}),
          })
        ).data,
      ),
    // Refused too: someone else may have changed it.
    onSettled: refresh,
  })
}

export const useCreateShare = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async (share: NewShare) => unwrap((await http.post<ApiEnvelope<SharedInsight>>(path(workspaceId, '/shared-insights'), share)).data),
    onSuccess: refresh,
  })
}

export const useMarkShareRead = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap((await http.post<ApiEnvelope<SharedInsight>>(path(workspaceId, `/shared-insights/${encodeURIComponent(id)}/read`))).data),
    onSuccess: refresh,
  })
}

export const useObjectives = (workspaceId: MaybeRefOrGetter<string>, filter: MaybeRefOrGetter<ObjectiveFilter>, enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: computed(() => actionKeys.objectives(toValue(workspaceId), toValue(filter))),
    queryFn: async () =>
      unwrap((await http.get<ApiEnvelope<MetricTarget[]>>(path(workspaceId, '/objectives'), { params: { status: toValue(filter) } })).data),
    enabled: computed(() => toValue(enabled)),
    retry: retryUnlessRefused,
  })

export const useCreateObjective = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async (objective: NewObjective) =>
      unwrap((await http.post<ApiEnvelope<MetricTarget>>(path(workspaceId, '/objectives'), objective)).data),
    onSuccess: refresh,
  })
}

export const useUpdateObjective = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async ({ id, change }: { id: string; change: ObjectiveChange }) =>
      unwrap((await http.patch<ApiEnvelope<MetricTarget>>(path(workspaceId, `/objectives/${encodeURIComponent(id)}`), change)).data),
    onSettled: refresh,
  })
}

export const useMarkObjectiveReviewed = (workspaceId: MaybeRefOrGetter<string>) => {
  const refresh = useRefresh(workspaceId)
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap((await http.post<ApiEnvelope<MetricTarget>>(path(workspaceId, `/objectives/${encodeURIComponent(id)}/review`))).data),
    onSuccess: refresh,
  })
}
